import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { PAGE_SEO, SITE_CONFIG } from '../data/seoConfig';
import { CASE_STUDIES } from '../mock';

export default function CaseStudies() {
  useSEO(PAGE_SEO.caseStudies);

  useJsonLd('case-studies-schema', [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Ransen Case Studies',
      itemListElement: CASE_STUDIES.map((cs, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_CONFIG.url}/case-studies/${cs.slug}`,
        name: cs.title,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
        { '@type': 'ListItem', position: 2, name: 'Case Studies', item: `${SITE_CONFIG.url}/case-studies` },
      ],
    },
  ]);

  return (
    <div data-testid='case-studies-page'>
      <PageHeader
        eyebrow='Case Studies'
        title={<><span className='text-white'>Digital marketing outcomes we’re</span> <span className='text-brand-gradient'>proud to publish</span></>}
        description='Real growth programs, real dashboards, real revenue. Client case studies across ecommerce, SaaS, real estate, and B2B — with the methodology behind each win.'
        crumbs={[{ label: 'Case Studies' }]}
      />
      <section className='py-16'>
        <motion.div
          className='max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8'
          initial='hidden'
          whileInView='show'
          viewport={{ once: true, margin: '-80px' }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.09 } },
          }}
        >
          {CASE_STUDIES.map((cs) => (
            <motion.div
              key={cs.slug}
              variants={{
                hidden: { opacity: 0, y: 24 },
                show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.2, 0.7, 0.2, 1] } },
              }}
            >
              <Link
                to={`/case-studies/${cs.slug}`}
                data-testid={`case-study-card-${cs.slug}`}
                className='block bg-white text-slate-900 rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-black/5 overflow-hidden group transition-transform duration-500 hover:-translate-y-1'
              >
                <div className='relative aspect-[16/9] overflow-hidden'>
                  <img src={cs.image} alt={cs.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                  <div className='absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[11px] tracking-wider uppercase'>{cs.industry}</div>
                  <div className='absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 border border-black/5 backdrop-blur flex items-center justify-center text-slate-900 group-hover:bg-brand group-hover:text-white transition-colors'>
                    <ArrowUpRight className='w-4 h-4'/>
                  </div>
                </div>
                <div className='px-7 py-8'>
                  <h3 className='font-heading text-2xl font-semibold text-slate-900 leading-snug group-hover:text-brand transition-colors'>{cs.title}</h3>
                  <div className='mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/20 text-brand text-sm font-semibold'>
                    {cs.metric}
                  </div>
                  <p className='mt-4 text-[15.5px] text-slate-700 leading-[1.75] font-body'>{cs.summary}</p>
                  <div className='mt-6 pt-5 border-t border-slate-100 flex flex-wrap gap-2'>
                    {cs.tags.map((t) => (
                      <span key={t} className='px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200 text-[11.5px] text-slate-600'>{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <div className='mt-16 text-center'>
          <Link to='/contact' className='btn-primary text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2' data-testid='case-studies-cta'>
            Request a full case study
          </Link>
        </div>
      </section>
    </div>
  );
}
